import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../global/AuthContext";
import {
  activeNavigationGroup,
  compactNavigationQuery,
  navigationGroups,
  navigationItemActive,
} from "../../global/navigation";
import "../../assets/css/container/main/Top.css";

export default function Top({
  onMenuClick,
  menuOpen = false,
}: {
  onMenuClick: () => void;
  menuOpen?: boolean;
}) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState<string | null>(null);
  const header = useRef<HTMLElement>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});
  const groups = navigationGroups(
    !!user,
    user?.role?.toLowerCase() === "admin",
  );
  const active = activeNavigationGroup(location.pathname);
  const currentLabel =
    groups.find((group) => group.id === active)?.label || "팩트·토론";

  useEffect(() => {
    setOpen(null);
  }, [location, user]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) setOpen(null);
    };
    const resize = () => {
      if (window.matchMedia(compactNavigationQuery).matches) setOpen(null);
    };
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", resize);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <header
      className="Top"
      ref={header}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node))
          setOpen(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          triggers.current[open]?.focus();
          setOpen(null);
        }
      }}
    >
      <div className="top-mobile">
        <button
          className="menuBtn"
          aria-label="전체 메뉴 열기"
          aria-expanded={menuOpen}
          aria-controls="site-sidebar"
          onClick={onMenuClick}
        >
          ☰
        </button>
        <Link className="top-logo" to="/" state={{ initialType: "recent" }}>
          FactWeb
        </Link>
        <span className="top-current-area">{currentLabel}</span>
      </div>
      <div className="top-desktop">
        <Link className="top-logo" to="/" state={{ initialType: "recent" }}>
          FactWeb
        </Link>
        <nav className="top-nav" aria-label="주 메뉴">
          {groups.map((group) => (
            <div
              className={`top-group ${group.id === "account" ? "top-account" : ""}`}
              key={group.id}
            >
              <button
                ref={(element) => {
                  triggers.current[group.id] = element;
                }}
                className={`top-group-trigger ${active === group.id ? "active" : ""}`}
                aria-expanded={open === group.id}
                aria-controls={`top-links-${group.id}`}
                onClick={() => setOpen(open === group.id ? null : group.id)}
              >
                {group.label}
                <span className="top-chevron" aria-hidden="true">
                  {open === group.id ? "▴" : "▾"}
                </span>
              </button>
              {open === group.id && (
                <div className="top-dropdown" id={`top-links-${group.id}`}>
                  {group.items.map((item) =>
                    item.logout ? (
                      <button
                        key={item.label}
                        className="top-logout"
                        onClick={() => {
                          setOpen(null);
                          logout();
                        }}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={item.label}
                        to={item.path}
                        state={
                          item.initialType
                            ? { initialType: item.initialType }
                            : undefined
                        }
                        aria-current={
                          navigationItemActive(
                            item,
                            location.pathname,
                            location.state,
                          )
                            ? "page"
                            : undefined
                        }
                        onClick={() => setOpen(null)}
                      >
                        {item.label}
                      </Link>
                    ),
                  )}
                </div>
              )}
            </div>
          ))}
          {!loading && !user && (
            <Link className="top-login" to="/login">
              로그인
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
